/* eslint-disable no-plusplus */
/* eslint-disable react/jsx-no-bind */
import React, { useState, useEffect } from "react";
import { FiEdit3, FiTrash2 } from "react-icons/fi";
import SignaturePad from "react-signature-canvas";
import { Modal, ModalButton } from "../../../components/Modal";

interface ModalAddSignatureLocalProps {
  type: string;
  setModalAddSignature(showModal: boolean): void;
  setTechnicianSignature(signature: boolean): void;
  setResponsibleSignature(signature: boolean): void;
  onSaveSignature(signatureData: string, signatureType: string): void;
}

export function ModalAddSignatureLocal({
  type,
  setModalAddSignature,
  setTechnicianSignature,
  setResponsibleSignature,
  onSaveSignature,
}: ModalAddSignatureLocalProps) {
  const [loading, setLoading] = useState(false);
  const signCanvas =
    React.useRef() as React.MutableRefObject<SignaturePad | null>;
  const clearSignature = () => signCanvas.current?.clear();

  // Corrigir problema de redimensionamento no iOS
  useEffect(() => {
    const resizeCanvas = () => {
      if (signCanvas.current) {
        const canvas = signCanvas.current.getCanvas();
        const ratio = Math.max(window.devicePixelRatio || 1, 1);
        const rect = canvas.getBoundingClientRect();
        canvas.width = rect.width * ratio;
        canvas.height = rect.height * ratio;
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.scale(ratio, ratio);
        }
      }
    };

    // Aguardar o modal abrir completamente
    const timer = setTimeout(resizeCanvas, 100);
    window.addEventListener('resize', resizeCanvas);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', resizeCanvas);
    };
  }, []);

  async function saveSignatureLocal() {
    try {
      setLoading(true);

      // Verificar se há uma assinatura
      if (!signCanvas.current || signCanvas.current.isEmpty()) {
        setLoading(false);
        return;
      }

      // Obter dados da assinatura como base64
      // Usar getTrimmedCanvas() para remover espaços em branco
      const trimmedCanvas = signCanvas.current.getTrimmedCanvas();

      // Para iOS: Criar um novo canvas com fundo branco para garantir compatibilidade
      const outputCanvas = document.createElement('canvas');
      outputCanvas.width = trimmedCanvas.width;
      outputCanvas.height = trimmedCanvas.height;
      const ctx = outputCanvas.getContext('2d');

      if (ctx) {
        // Fundo branco
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, outputCanvas.width, outputCanvas.height);
        // Desenhar a assinatura por cima
        ctx.drawImage(trimmedCanvas, 0, 0);
      }

      // Gerar base64 com qualidade otimizada para iOS
      const signatureData = outputCanvas.toDataURL("image/png", 1.0);

      // Validar que o base64 foi gerado corretamente
      if (!signatureData || !signatureData.startsWith('data:image')) {
        console.error('Falha ao gerar assinatura');
        setLoading(false);
        return;
      }

      // Salvar localmente através do callback
      onSaveSignature(signatureData, type);

      setModalAddSignature(false);

      if (type === "technician_signature") {
        setTechnicianSignature(true);
      } else if (type === "responsible_signature") {
        setResponsibleSignature(true);
      }

      setLoading(false);
    } catch (e) {
      console.error('Erro ao salvar assinatura:', e);
      setLoading(false);
      setModalAddSignature(false);
    }
  }

  const title =
    type === "responsible_signature"
      ? "Assinatura Cliente"
      : "Assinatura Técnico";

  return (
    <>
      {/* Loading Overlay */}
      {loading && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60]">
          <div className="bg-white/10 backdrop-blur-sm p-4 rounded-lg flex items-center space-x-3">
            <div className="animate-spin h-6 w-6 border-3 border-t-transparent border-white rounded-full" />
            <span className="text-white font-medium">Carregando...</span>
          </div>
        </div>
      )}

      <Modal
        isOpen
        onClose={() => setModalAddSignature(false)}
        title={title}
        icon={<FiEdit3 size={24} />}
        variant="primary"
        footer={
          <>
            <ModalButton
              variant="cancel"
              onClick={() => setModalAddSignature(false)}
            >
              Cancelar
            </ModalButton>
            <ModalButton variant="success" onClick={saveSignatureLocal}>
              Salvar
            </ModalButton>
          </>
        }
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Desenhe a assinatura no campo abaixo:
          </p>

          {/* Signature Pad Container */}
          <div className="bg-gray-100 p-3 rounded-lg">
            <SignaturePad
              ref={signCanvas}
              clearOnResize={false}
              canvasProps={{
                className:
                  "bg-white rounded-lg w-full h-48 shadow-inner border border-gray-200",
                style: {
                  touchAction: 'none', // Importante para iOS - evita scroll durante desenho
                },
              }}
            />

            {/* Clear Button */}
            <div className="mt-3 flex justify-end">
              <button
                type="button"
                onClick={clearSignature}
                className="px-3 py-1.5 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg transition-colors flex items-center gap-1 text-sm"
              >
                <FiTrash2 size={14} />
                Limpar
              </button>
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
}
